// src\app\models\order.model.ts

import { Coupon } from "../interfaces/coupon.interface";
import { Item, ItemVariation } from "../interfaces/item.interface";
import { Restaurant } from "../interfaces/restaurant.interface";
import { Address } from "./address.model";
import { User } from "./user.model";

// Interface for order items to properly type the variations
export interface OrderItem extends Omit<Item, 'variations'> {
  variations?: ItemVariation[];
  quantity?: number; // Keep for backward compatibility
  total_price?: number;
}

export class Order {
    constructor(
        public address: Address,
        public restaurant_id: string,
        public user: any,
        public order: OrderItem[],  // Updated to use OrderItem
        public total: number,
        public grandTotal: number,
        public deliveryCharge: number,
        public status: string,
        public payment_status: boolean,
        public payment_mode: string,
        public discount: number,
        public distance: number,
        public created_at: Date,
        public placed_at: string,
        public updated_at: Date,
        public tax?: number,
        public wallet_discount?: number,
        public id?: string,
        public uid?: string,
        public instruction?: string,
        public restaurant?: Restaurant,
        public coupon?: Coupon,
        public payment_id?: string,
        public cancel_reason?: string,
        public rating?: {
            rate_restaurant?: number,
            comment_restaurant?: string,
            rate_rider?: number,
            comment_rider?: string
        },
        public rider?: User,
        public rider_id?: string,
    ) {
        // Initialize any items without variations to have a default variation
        this.order = this.order.map(item => {
            if (!item.variations && typeof item.quantity === 'number') {
                return {
                    ...item,
                    variations: [{
                        id: 'default',
                        quantity: item.quantity,
                        total_price: item.price * item.quantity
                    }]
                };
            }
            return item;
        });
    }

    // Helper method to get total quantity of an item including all variations
    getTotalQuantity(item: OrderItem): number {
        if (!item.variations?.length) {
            return item.quantity || 0;
        }
        return item.variations.reduce((total, variation) => 
            total + (variation.quantity || 0), 0);
    }

    // Helper method to get total price of an item including all variations
    getTotalPrice(item: OrderItem): number {
        if (!item.variations?.length) {
            return (item.price || 0) * (item.quantity || 0);
        }
        return item.variations.reduce((total, variation) => 
            total + (variation.total_price || 0), 0);
    }

    // Method to get formatted variation details
    getVariationDetails(variation: ItemVariation): string {
        const parts: string[] = [];
        
        if (variation.selected_size) {
            parts.push(variation.selected_size.name);
        }
        
        if (variation.selected_supplements?.length) {
            const supplements = variation.selected_supplements
                .map(s => `${s.name} (${s.selected_quantity})`)
                .join(', ');
            parts.push(supplements);
        }
        
        return parts.join(' + ');
    }

    // Method to check if any items have variations
    hasVariations(): boolean {
        return this.order.some(item => 
            item.variations && item.variations.length > 0);
    }

    // Convert legacy order items to new format
    static convertLegacyOrder(orderData: any): Order {
        // Ensure order items have the new structure
        const updatedOrder = orderData.order.map(item => {
            if (!item.variations && typeof item.quantity === 'number') {
                return {
                    ...item,
                    variations: [{
                        id: 'default',
                        quantity: item.quantity,
                        total_price: item.price * item.quantity
                    }]
                };
            }
            return item;
        });

        // Create new order with converted data
        return new Order(
            orderData.address,
            orderData.restaurant_id,
            orderData.user,
            updatedOrder,
            orderData.total,
            orderData.grandTotal,
            orderData.deliveryCharge,
            orderData.status,
            orderData.payment_status,
            orderData.payment_mode,
            orderData.discount,
            orderData.distance,
            orderData.created_at,
            orderData.placed_at,
            orderData.updated_at,
            orderData.tax,
            orderData.wallet_discount,
            orderData.id,
            orderData.uid,
            orderData.instruction,
            orderData.restaurant,
            orderData.coupon,
            orderData.payment_id,
            orderData.cancel_reason,
            orderData.rating,
            orderData.rider,
            orderData.rider_id
        );
    }
}